import React from 'react'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { fetchScopeDiagnostics } from '../../services/complianceExecutionApi'
import type { ScopeAuthority } from '../../services/complianceExecutionApi'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'

interface Props {
  standardCode: string
  scopeAuthority?: ScopeAuthority | null
}

export default function ScopeAuthorityDiagnosticsPanel({ standardCode, scopeAuthority }: Props) {
  const { t } = useI18n()
  const [diag, setDiag] = React.useState<Awaited<ReturnType<typeof fetchScopeDiagnostics>> | null>(null)

  React.useEffect(() => {
    if (!featureFlags.COMPLIANCE_SCOPE_AUTHORITY_V1) return
    void fetchScopeDiagnostics(standardCode)
      .then(setDiag)
      .catch(() => setDiag(null))
  }, [standardCode, scopeAuthority?.scopeRevision])

  if (!featureFlags.COMPLIANCE_SCOPE_AUTHORITY_V1) return null

  const mismatches = [...(scopeAuthority?.mismatches || []), ...(diag?.mismatches || [])]

  if (!mismatches.length && !scopeAuthority?.staleDiscovery) return null

  return (
    <section className={wfStyles.innerStack}>
      <p className={wfStyles.itemTitle}>
        {t('complianceWorkflow.scopeAuthorityWarning', 'Scope authority')}
      </p>
      {scopeAuthority?.staleDiscovery ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceWorkflow.scopeChangedRerunDiscovery', 'Scope changed. Run discovery again.')}
          {scopeAuthority.scopeRevision != null
            ? ` (${t('complianceWorkflow.scopeRevision', 'revision')} ${scopeAuthority.scopeRevision})`
            : ''}
        </p>
      ) : null}
      {mismatches.slice(0, 5).map((m, i) => (
        <p key={i} className={wfStyles.itemMeta}>
          <strong>{m.type}</strong>
          {m.systemName ? `: ${m.systemName}` : ''} — {m.recommendation || m.message}
        </p>
      ))}
    </section>
  )
}
